import { z } from "zod";
const UPPERCASE_ULID_PATTERN = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;
const CHECKOUT_REFERENCE_PATTERN = /^TU-[0-7][0-9A-HJKMNP-TV-Z]{25}$/;
const REFUND_REFERENCE_PATTERN = /^TU-RF-[0-7][0-9A-HJKMNP-TV-Z]{25}$/;
const MAX_JSON_BYTES = 65_536;
export const billingRailSchema = z.discriminatedUnion("provider", [
    z.strictObject({ provider: z.literal("xendit"), transport: z.literal("sendit") }),
    z.strictObject({ provider: z.literal("polar"), transport: z.literal("polar") }),
]);
export const uppercaseUlidSchema = z.string().regex(UPPERCASE_ULID_PATTERN);
export const checkoutReferenceSchema = z.string().regex(CHECKOUT_REFERENCE_PATTERN);
export const refundReferenceSchema = z.string().regex(REFUND_REFERENCE_PATTERN);
export const currencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);
export const moneySchema = z.strictObject({
    amount: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    currency: currencyCodeSchema,
});
const productSnapshotLimits = {
    maxDepth: 6,
    maxFields: 128,
    maxArrayLength: 128,
    maxStringLength: 4_096,
    maxKeyLength: 128,
};
const normalizedFactsLimits = {
    maxDepth: 4,
    maxFields: 64,
    maxArrayLength: 64,
    maxStringLength: 2_048,
    maxKeyLength: 128,
};
export const productSnapshotSchema = boundedJsonObjectSchema(productSnapshotLimits, MAX_JSON_BYTES, "invalid product snapshot");
const normalizedEventFactsSchema = boundedJsonObjectSchema(normalizedFactsLimits, undefined, "invalid normalized event facts");
export const checkoutTargetSchema = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("access_pass"), planPriceId: uppercaseUlidSchema }),
    z.strictObject({ type: z.literal("credit_pack"), creditPackId: uppercaseUlidSchema }),
]);
export const idempotencyKeySchema = z.string().min(8).max(128);
export const browserCheckoutInputSchema = z.strictObject({
    target: checkoutTargetSchema,
    idempotencyKey: idempotencyKeySchema,
    guardianRelationshipId: z.uuid().max(36).optional(),
});
export const mcpCheckoutInputSchema = z.strictObject({
    target: checkoutTargetSchema,
    idempotencyKey: idempotencyKeySchema,
});
const boundedProviderIdSchema = z.string().min(1).max(512);
const boundedEventTypeSchema = z.string().min(1).max(128);
const timestampSchema = z
    .number()
    .int()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER)
    .refine((value) => !Number.isNaN(new Date(value).getTime()), { error: "Invalid timestamp" });
const httpsUrlSchema = z
    .url()
    .max(2_048)
    .refine((value) => isSafeUrl(value, false), { error: "URL must use HTTPS" });
const providerEventCommonShape = {
    rail: billingRailSchema,
    providerEventId: boundedProviderIdSchema,
    eventType: boundedEventTypeSchema,
    normalizationVersion: z.number().int().min(1).max(1_000_000),
    facts: normalizedEventFactsSchema.optional(),
};
const providerEventBaseShape = {
    ...providerEventCommonShape,
    resourceId: boundedProviderIdSchema,
};
const providerIdentityShape = {
    providerCheckoutId: boundedProviderIdSchema.optional(),
    externalProductId: boundedProviderIdSchema.optional(),
    externalPriceId: boundedProviderIdSchema.optional(),
};
const paymentEventShape = {
    ...providerEventBaseShape,
    ...providerIdentityShape,
    checkoutReference: checkoutReferenceSchema,
    money: moneySchema,
    providerOrderId: boundedProviderIdSchema.optional(),
    providerPaymentId: boundedProviderIdSchema.optional(),
    providerCustomerId: boundedProviderIdSchema.optional(),
    paidAtMs: timestampSchema.optional(),
    receiptUrl: httpsUrlSchema.optional(),
    providerRefundableAmount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
};
export const normalizedProviderEventSchema = z
    .union([
    z.strictObject({
        kind: z.literal("checkout"),
        ...providerEventBaseShape,
        ...providerIdentityShape,
        providerCheckoutId: boundedProviderIdSchema,
        money: moneySchema,
        status: z.enum(["provider_created", "pending", "paid", "expired", "failed", "cancelled"]),
        checkoutReference: checkoutReferenceSchema,
        redirectUrl: httpsUrlSchema.optional(),
        expiresAtMs: timestampSchema.optional(),
    }),
    z.strictObject({
        kind: z.literal("payment"),
        ...paymentEventShape,
        status: z.literal("paid"),
        providerCheckoutId: boundedProviderIdSchema,
        providerOrderId: boundedProviderIdSchema,
        paidAtMs: timestampSchema,
    }),
    z.strictObject({
        kind: z.literal("payment"),
        ...paymentEventShape,
        status: z.enum(["failed", "expired", "disputed", "chargeback"]),
    }),
    z.strictObject({
        kind: z.literal("refund"),
        ...providerEventBaseShape,
        status: z.enum(["pending", "succeeded", "failed", "cancelled"]),
        checkoutReference: checkoutReferenceSchema.optional(),
        refundReference: refundReferenceSchema.optional(),
        money: moneySchema,
        providerRefundId: boundedProviderIdSchema,
        providerOrderId: boundedProviderIdSchema,
        reason: z.enum([
            "duplicate",
            "fraudulent",
            "customer_request",
            "service_disruption",
            "satisfaction_guarantee",
            "other",
            "dispute_prevention",
        ]),
        completedAtMs: timestampSchema.optional(),
    }),
    z.strictObject({
        kind: z.literal("unknown"),
        ...providerEventCommonShape,
        resourceId: boundedProviderIdSchema.optional(),
        observedStatus: boundedEventTypeSchema.optional(),
    }),
])
    .superRefine((value, context) => {
    if (value.kind === "payment" && value.status === "paid") {
        if (value.rail.provider === "polar" && value.externalProductId === undefined) {
            context.addIssue({ code: "custom", message: "Polar paid event requires product ID" });
        }
        if (value.rail.provider === "polar" && value.providerRefundableAmount === undefined) {
            context.addIssue({ code: "custom", message: "Polar paid event requires refundable amount" });
        }
    }
    if (utf8Length(JSON.stringify(value)) > MAX_JSON_BYTES) {
        context.addIssue({ code: "custom", message: "normalized event exceeds 64 KiB" });
    }
});
export const webhookVerificationEvidenceSchema = z.strictObject({
    payloadHash: z.string().regex(/^[0-9a-f]{64}$/),
    verificationKeyVersion: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
});
export const verifiedBillingEventSchema = z.strictObject({
    event: normalizedProviderEventSchema,
    evidence: webhookVerificationEvidenceSchema,
});
export const billingErrorSchema = z.strictObject({
    code: z.enum([
        "invalid_request",
        "provider_unavailable",
        "provider_terminal",
        "provider_ambiguous",
        "verification_failed",
        "invalid_provider_response",
    ]),
    message: z.string().min(1).max(1_000).optional(),
    operatorReview: z.boolean().optional(),
});
export const billingResultSchema = z.discriminatedUnion("ok", [
    z.strictObject({ ok: z.literal(true) }),
    z.strictObject({ ok: z.literal(false), error: billingErrorSchema }),
]);
export const billingCursorSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/)
    .min(1)
    .max(256);
export const billingHistoryQuerySchema = z.strictObject({
    cursor: billingCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const mcpBillingHistoryInputSchema = z.strictObject({
    cursor: billingCursorSchema.optional(),
    limit: z.number().int().min(1).max(50).default(20),
});
const publicTimestampSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const publicHttpsUrlSchema = z
    .url()
    .max(2_048)
    .refine((value) => isSafeUrl(value, true), { error: "URL must use HTTPS except on localhost" });
const nonNegativeSafeIntegerSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
export const billingEntitlementSchema = z.strictObject({
    id: uppercaseUlidSchema,
    status: z.enum(["active", "scheduled", "expired", "revoked", "refunded"]),
    planName: z.string().trim().min(1).max(200),
    startsAt: publicTimestampSchema,
    endsAt: publicTimestampSchema,
    monthlyQuota: nonNegativeSafeIntegerSchema,
});
export const billingCreditLotSchema = z.strictObject({
    id: z.string().min(1).max(128),
    originalUnits: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    remainingUnits: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    expiresAt: publicTimestampSchema,
});
export const billingUsageSchema = z.strictObject({
    source: z.enum(["free", "entitlement"]),
    used: nonNegativeSafeIntegerSchema,
    limit: nonNegativeSafeIntegerSchema,
    remaining: nonNegativeSafeIntegerSchema,
    periodStart: publicTimestampSchema,
    periodEnd: publicTimestampSchema,
});
export const guardianBillingBeneficiarySchema = z.strictObject({
    guardianRelationshipId: z.uuid().max(36),
    label: z.string().trim().min(1).max(100),
});
export const billingCheckoutSummarySchema = z.strictObject({
    checkoutId: uppercaseUlidSchema,
    status: z.enum([
        "creating",
        "reconciling",
        "provider_created",
        "pending",
        "paid",
        "paid_capacity_conflict",
        "expired",
        "failed",
        "cancelled",
    ]),
    redirectUrl: publicHttpsUrlSchema.nullable(),
    expiresAt: publicTimestampSchema,
});
export const billingStatusResponseSchema = z.strictObject({
    freeQuota: z.strictObject({
        limit: nonNegativeSafeIntegerSchema,
        used: nonNegativeSafeIntegerSchema,
        remaining: nonNegativeSafeIntegerSchema,
        periodStart: publicTimestampSchema,
        periodEnd: publicTimestampSchema,
    }),
    entitlement: z
        .strictObject({
        active: billingEntitlementSchema.optional(),
        scheduled: billingEntitlementSchema.optional(),
    })
        .optional(),
    usage: billingUsageSchema,
    creditLots: z.array(billingCreditLotSchema).max(100),
    purchasable: z.boolean(),
    accountRestricted: z.boolean(),
    checkoutDisabledReason: z.enum(["billing_disabled", "account_restricted"]).optional(),
    billingUrl: publicHttpsUrlSchema,
    guardianBeneficiary: guardianBillingBeneficiarySchema.optional(),
    latestCheckout: billingCheckoutSummarySchema.optional(),
});
export const refundSupportSummarySchema = z.strictObject({
    requestId: uppercaseUlidSchema,
    status: z.enum(["open", "resolved", "spam", "closed"]),
    createdAt: publicTimestampSchema,
});
export const paymentRefundSummarySchema = z.strictObject({
    id: uppercaseUlidSchema,
    status: z.enum(["creating", "provider_created", "pending", "succeeded", "failed", "cancelled"]),
    money: moneySchema,
    createdAt: publicTimestampSchema,
    completedAt: publicTimestampSchema.nullable(),
});
export const paymentHistoryItemSchema = z.strictObject({
    id: uppercaseUlidSchema,
    status: z.enum([
        "pending",
        "paid",
        "partially_refunded",
        "refunded",
        "disputed",
        "chargeback",
        "failed",
        "expired",
    ]),
    product: z.strictObject({
        type: z.enum(["access_pass", "credit_pack"]),
        name: z.string().trim().min(1).max(200),
    }),
    money: moneySchema,
    refundedAmount: nonNegativeSafeIntegerSchema,
    receiptUrl: publicHttpsUrlSchema.nullable(),
    paidAt: publicTimestampSchema.nullable(),
    createdAt: publicTimestampSchema,
    effect: z
        .strictObject({
        type: z.enum(["entitlement", "credit_lot", "unfulfilled"]),
        status: z.string().min(1).max(64),
    })
        .nullable(),
    refunds: z.array(paymentRefundSummarySchema).max(50),
    refundSupport: refundSupportSummarySchema.nullable(),
    refundSupportAvailable: z.boolean(),
});
export const paymentHistoryResponseSchema = z.strictObject({
    items: z.array(paymentHistoryItemSchema).max(50),
    nextCursor: billingCursorSchema.optional(),
});
export const checkoutResponseSchema = billingCheckoutSummarySchema.extend({
    redirectUrl: publicHttpsUrlSchema,
});
export const refundSupportResponseSchema = z.strictObject({
    requestId: uppercaseUlidSchema,
    status: z.literal("open"),
    existing: z.boolean(),
});
export const adminPaymentResponseSchema = z.strictObject({
    payment: paymentHistoryItemSchema,
    payerUserId: z.string().min(1).max(512),
    beneficiaryOwnerId: z.string().min(1).max(512),
    rail: billingRailSchema,
    refundDispatch: z
        .strictObject({
        refundId: uppercaseUlidSchema,
        state: z.enum([
            "never_called",
            "possibly_called",
            "reconcile_pending",
            "provider_recorded",
            "terminal",
        ]),
        attemptCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
        lastError: z.string().max(1_000).nullable(),
        updatedAt: publicTimestampSchema.nullable(),
    })
        .nullable(),
});
const adminRefundReasonSchema = z.enum([
    "duplicate",
    "fraudulent",
    "customer_request",
    "service_disruption",
    "satisfaction_guarantee",
    "other",
]);
const checkoutRecoveryProviderSchema = z.enum(["xendit", "polar"]);
const checkoutRecoveryOutcomeSchema = z.enum(["provider_absent", "final_non_payable"]);
export const adminCheckoutRecoveryEvidenceSchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("provider_case"),
        provider: checkoutRecoveryProviderSchema,
        outcome: checkoutRecoveryOutcomeSchema,
        providerCase: z.string().trim().min(1).max(500),
        providerReference: z.string().trim().min(1).max(512).optional(),
    }),
    z.strictObject({
        type: z.literal("provider_check"),
        provider: checkoutRecoveryProviderSchema,
        outcome: checkoutRecoveryOutcomeSchema,
        checkedAt: publicTimestampSchema,
        source: z.enum(["provider_api", "provider_dashboard", "provider_support"]),
        providerReference: z.string().trim().min(1).max(512).optional(),
    }),
]);
export const manualRefundConfirmationEvidenceSchema = z.strictObject({
    providerReferenceId: z.string().trim().min(1).max(512),
    confirmedAmount: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    confirmedCurrency: currencyCodeSchema,
    confirmedAt: publicTimestampSchema,
    source: z.enum(["sendit_support", "xendit_dashboard", "xendit_support"]),
    operatorNote: z.string().trim().min(8).max(1_000),
});
export const adminCheckoutRecoveryInputSchema = z.strictObject({
    expectedVersion: z
        .number()
        .int()
        .min(0)
        .max(Number.MAX_SAFE_INTEGER - 1),
    reason: z.string().trim().min(8).max(1_000),
    evidence: adminCheckoutRecoveryEvidenceSchema,
});
export const adminCheckoutRecoveryResponseSchema = z.strictObject({
    checkoutId: uppercaseUlidSchema,
    status: z.enum(["failed", "expired", "cancelled"]),
    version: nonNegativeSafeIntegerSchema,
    reservationReleased: z.boolean(),
});
export const adminRefundInputSchema = z.discriminatedUnion("action", [
    z.strictObject({
        action: z.literal("create"),
        idempotencyKey: idempotencyKeySchema,
        reason: adminRefundReasonSchema,
        confirmation: z.literal("refund_remaining_value"),
        target: z.discriminatedUnion("type", [
            z.strictObject({ type: z.literal("entitlement"), entitlementId: uppercaseUlidSchema }),
            z.strictObject({ type: z.literal("credit_lot"), creditLotId: z.string().min(1).max(128) }),
            z.strictObject({ type: z.literal("unfulfilled") }),
        ]),
    }),
    z.strictObject({
        action: z.literal("confirm_manual"),
        refundId: uppercaseUlidSchema,
        expectedVersion: nonNegativeSafeIntegerSchema,
        reason: z.string().trim().min(8).max(1_000),
        confirmation: z.literal("provider_refund_confirmed"),
        evidence: manualRefundConfirmationEvidenceSchema,
    }),
]);
export const adminEntitlementRevokeInputSchema = z.strictObject({
    expectedUpdatedAt: publicTimestampSchema,
    reason: z.string().trim().min(8).max(1_000),
    confirmation: z.literal("revoke_entitlement_access"),
});
const adminRefundResponseBase = {
    refundId: uppercaseUlidSchema,
    status: z.enum(["creating", "provider_created", "pending", "succeeded", "failed", "cancelled"]),
    replay: z.boolean(),
};
export const adminRefundResponseSchema = z.discriminatedUnion("handling", [
    z.strictObject({ ...adminRefundResponseBase, handling: z.literal("manual_confirmation") }),
    z.strictObject({
        ...adminRefundResponseBase,
        handling: z.literal("manual_dashboard"),
        providerOrderId: z.string().min(1).max(512),
        referenceCode: refundReferenceSchema,
        money: moneySchema,
        priority: z.enum(["normal", "urgent"]),
        confirmation: z.literal("polar_webhook"),
    }),
]);
export const adminEntitlementRevokeResponseSchema = z.strictObject({
    entitlementId: uppercaseUlidSchema,
    status: z.literal("revoked"),
    replay: z.boolean(),
});
function boundedJsonObjectSchema(limits, maxBytes, message) {
    return z
        .unknown()
        .superRefine((value, context) => {
        const error = jsonObjectError(value, limits);
        if (error !== undefined)
            context.addIssue({ code: "custom", message: `${message}: ${error}` });
        if (error === undefined &&
            maxBytes !== undefined &&
            utf8Length(JSON.stringify(value)) > maxBytes) {
            context.addIssue({ code: "custom", message: `${message}: exceeds 64 KiB` });
        }
    })
        .transform((value) => value);
}
function jsonObjectError(value, limits) {
    if (!isPlainObject(value))
        return "must be a plain object";
    const state = { fields: 0, seen: new Set() };
    return jsonValueError(value, 0, limits, state);
}
function jsonValueError(value, depth, limits, state) {
    if (depth > limits.maxDepth)
        return "maximum depth exceeded";
    if (value === null || typeof value === "boolean")
        return undefined;
    if (typeof value === "number")
        return Number.isFinite(value) ? undefined : "number must be finite";
    if (typeof value === "string") {
        return value.length <= limits.maxStringLength ? undefined : "string too long";
    }
    if (typeof value !== "object")
        return "contains a non-JSON value";
    if (state.seen.has(value))
        return "contains a cycle";
    state.seen.add(value);
    if (Array.isArray(value)) {
        if (value.length > limits.maxArrayLength)
            return "array too long";
        for (const item of value) {
            const error = jsonValueError(item, depth + 1, limits, state);
            if (error !== undefined)
                return error;
        }
        return undefined;
    }
    if (!isPlainObject(value))
        return "contains a non-plain object";
    const entries = Object.entries(value);
    state.fields += entries.length;
    if (state.fields > limits.maxFields)
        return "too many fields";
    for (const [key, item] of entries) {
        if (key.length === 0 || key.length > limits.maxKeyLength)
            return "invalid field name";
        const error = jsonValueError(item, depth + 1, limits, state);
        if (error !== undefined)
            return error;
    }
    return undefined;
}
function isPlainObject(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function utf8Length(value) {
    let bytes = 0;
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0;
        bytes += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
    }
    return bytes;
}
function isSafeUrl(value, allowLocalHttp) {
    if (value.includes("#"))
        return false;
    const schemeEnd = value.indexOf("://");
    if (schemeEnd < 0)
        return false;
    const scheme = value.slice(0, schemeEnd);
    const authorityAndPath = value.slice(schemeEnd + 3);
    const authorityEnd = authorityAndPath.search(/[/?]/u);
    const authority = authorityAndPath.slice(0, authorityEnd < 0 ? undefined : authorityEnd);
    if (authority.includes("@"))
        return false;
    return (scheme === "https" ||
        (allowLocalHttp && scheme === "http" && /^(?:localhost)(?::\d+)?$/u.test(authority)));
}
//# sourceMappingURL=billing.js.map